import { MagnifyingGlass } from '@creditornot/cb-icons';
import { Input } from '@creditornot/cb-input';
import { MenuLinkItem } from '@creditornot/cb-menu';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { getAvailableLanguages, getLanguage, setLanguage } from 'locale';
import { UiLanguage, UiLanguageKey } from 'locale/languages';

const SearchInput = styled(Input).attrs({ icon: <MagnifyingGlass /> })`
  margin-block-end: 8px;
`;

const LanguagesWrapper = styled.div<{ maxHeight: number }>`
  max-height: ${({ maxHeight }) => maxHeight}px;
  overflow-y: auto;
`;

type LanguageItemsProps = {
  maxHeight: number;
  currentLanguage: UiLanguage;
  searchValue?: string;
  onLanguageSelect?: (key: UiLanguageKey) => void;
};

const LanguageItems = ({ searchValue, currentLanguage, onLanguageSelect, maxHeight }: LanguageItemsProps) => {
  const languages = useMemo(() => {
    if (searchValue) {
      const searchValueRegex = new RegExp(searchValue, 'gi');
      return getAvailableLanguages().filter(
        ({ key, label }) => label.match(searchValueRegex) || key.match(searchValueRegex),
      );
    } else {
      return getAvailableLanguages();
    }
  }, [searchValue]);

  return (
    <LanguagesWrapper maxHeight={maxHeight}>
      {languages.map(({ key, label }) => {
        return (
          <MenuLinkItem
            key={key}
            active={currentLanguage.key === key}
            onClick={() => {
              onLanguageSelect?.(key);
            }}
          >
            {label}
          </MenuLinkItem>
        );
      })}
    </LanguagesWrapper>
  );
};

type LanguageSelectorProps = {
  maxHeight?: number;
  onLanguageSelect?: (key: UiLanguageKey) => void;
};

const LanguageSelector = ({ onLanguageSelect, maxHeight = 500 }: LanguageSelectorProps) => {
  const currentLanguage = getLanguage();
  const [searchValue, setSearchValue] = useState('');

  const handleLanguageSelect = (key: UiLanguageKey) => {
    setLanguage(key);
    onLanguageSelect?.(key);
  };

  return (
    <span>
      <SearchInput
        onClearClick={() => setSearchValue('')}
        onChange={e => setSearchValue(e.target.value)}
        value={searchValue}
      />
      <LanguageItems
        maxHeight={maxHeight}
        currentLanguage={currentLanguage}
        searchValue={searchValue}
        onLanguageSelect={handleLanguageSelect}
      />
    </span>
  );
};

export default LanguageSelector;
